import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@et/schema';
import { fetchFacets, fetchLedger, queryKeys, type ContentRev } from '../../lib/api.js';
import { localePath } from '../../lib/locale.js';

/**
 * O que o acervo tem, numa linha, antes do grafico.
 *
 * A pagina abria com filtros e um grafico, e quem chegava nao via o que isto
 * e nem quanto tem. Os numeros vem da API e nao de uma constante — e o "zero
 * marcadas como derrotadas" e a unica linha que e ao mesmo tempo um numero e
 * uma regra: nao existe coluna para o contrario.
 *
 * Os numeros sao do acervo inteiro, de proposito. A contagem filtrada vive
 * na barra de filtros; esta e a resposta a "o que ha aqui".
 */
export function Ledger({ locale, rev }: { readonly locale: Locale; readonly rev: ContentRev }) {
  const { t } = useTranslation();
  const ledger = useQuery({
    queryKey: queryKeys.ledger(rev, locale),
    queryFn: () => fetchLedger(rev.version, locale),
    staleTime: 60_000,
  });
  const facets = useQuery({
    queryKey: queryKeys.facets(rev, locale),
    queryFn: () => fetchFacets(rev.version, locale),
  });
  const l = ledger.data;
  if (l === undefined) return null;

  const per = l.perReligion.map((r) => ({
    ...r, name: facets.data?.religions.find((x) => x.id === r.religionId)?.name ?? r.religionId,
  }));
  const [a, b] = per;
  const open = l.objections.total - l.objections.responded;

  return (
    <p className="et-ledger" data-testid="ledger">
      <span className="et-ledger__item" data-tone="evidence">
        <b>{l.evidence}</b> {t('ledger.evidence')}
      </span>
      {a !== undefined && b !== undefined && (
        <span className="et-ledger__item" data-tone="evidence" title={`${a.name} / ${b.name}`}>
          <b>{t('ledger.perTradition', { a: a.n, b: b.n })}</b>
        </span>
      )}
      <span className="et-ledger__item" data-tone="source">
        <b>{l.passages}</b> {t('ledger.passages')}
      </span>
      <Link className="et-ledger__item" data-tone="objection" to={localePath(locale, 'objections')}>
        <b>{l.objections.total}</b> {t('ledger.objections')}, {t('ledger.open', { count: open })}
      </Link>
      <span className="et-ledger__item et-ledger__item--rule" title={t('ledger.defeatedNote')}>
        <b>{l.objections.defeated}</b> {t('ledger.defeated')}
      </span>
    </p>
  );
}
